import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import 'svg2pdf.js';
import { PDFDocument } from 'pdf-lib';
import {getMiddleValue, roundImplantLength} from "../../utils/utils";
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewModalComponent } from '@shared/components';

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {
  _http = inject(HttpClient);
  _dialog = inject(MatDialog);
  _apiUrl = environment.apiUrl;
  _authHeaders = new HttpHeaders({
    Authorization: '',
  });

  puntoDeLaTabParaCartel: any

  private _nussVariantOverride: 'v1' | 'v2' | undefined = undefined;

  private curves: BehaviorSubject<Array<{ svg: string; text: string[]; color: string }>> = new BehaviorSubject<Array<{ svg: string; text: string[]; color: string }>>([]);

  constructor() { }

  clearCurves(): void {
    this.curves.next([]);
  }

  addCurve(svgString: string, text: string[] = [], color: string = '#000000'): void {
    const currentCurves = this.curves.value;
    this.curves.next([...currentCurves, { svg: svgString, text, color }]);
  }

  // Métodos deprecados, ahora se pasan los textos directamente a addCurve
  addTextSF(text: string[], curveTextColor: string): void { }
  addTextFL(text: string[], curveTextColor: string): void { }

  getCurves(): Observable<{ svg: string; text: string[]; color: string; }[]> {
    return this.curves.asObservable();
  }

  async exportSvgToPdf(fileName: string, pixelspacing: any, backgroundPdfUrl: string, puntoDeLaTabParaCartel: any, nussTemplateVariantOverride?: 'v1' | 'v2'): Promise<File> {
    this.puntoDeLaTabParaCartel = puntoDeLaTabParaCartel;
    this._nussVariantOverride = nussTemplateVariantOverride;
    try {
      const response = await fetch(backgroundPdfUrl);
      const bgBytes = await response.arrayBuffer();
      const bgDoc = await PDFDocument.load(bgBytes);
      const [firstPage] = bgDoc.getPages();
      const { width: bgW_pts, height: bgH_pts } = firstPage.getSize();

      // Convert pts to mm (1pt = 0.352778mm)
      const pageW = bgW_pts * 0.352778;
      const pageH = bgH_pts * 0.352778;

      // 1. Generar overlay con curvas (Páginas 1..N con background)
      const curves = this.curves.value;
      const overlayBytes = await this.createOverlayPdf(curves, pixelspacing, pageW, pageH);

      // 2. Mergear todo con pdf-lib
      const finalBlob = await this.mergeBackgroundWithOverlay(bgBytes, overlayBytes, fileName);

      // Abrir modal de previsualización
      this._dialog.open(PdfPreviewModalComponent, {
        data: {
          pdfBlob: finalBlob,
          fileName: fileName
        },
        maxWidth: '90vw',
        width: '1000px'
      });

      const file = new File([finalBlob], `${fileName}.pdf`, { type: 'application/pdf' });
      return file;
    } finally {
      this._nussVariantOverride = undefined;
    }
  }


  private parseSvgSize(svgString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = doc.documentElement;

    let width = parseFloat(svgElement.getAttribute('width') || '0');
    let height = parseFloat(svgElement.getAttribute('height') || '0');
    let viewBox = svgElement.getAttribute('viewBox');

    if (viewBox && (width === 0 || height === 0)) {
      const parts = viewBox.split(' ');
      if (parts.length === 4) {
        width = parseFloat(parts[2]);
        height = parseFloat(parts[3]);
      }
    }

    if (!viewBox && width && height) {
      viewBox = `0 0 ${width} ${height}`;
    }

    return { width, height, viewBox, svgElement };
  }

  private async createOverlayPdf(curves: any[], pixelspacing: any, pageW: number, pageH: number): Promise<Uint8Array> {
    const pdf = new jsPDF({
      orientation: pageW > pageH ? 'l' : 'p',
      unit: 'mm',
      format: [pageW, pageH]
    });

    for (let i = 0; i < curves.length; i++) {
      const curveData = curves[i];
      if (i > 0) {
        pdf.addPage([pageW, pageH], pageW > pageH ? 'l' : 'p');
      }

      // Dibujar bloques de información
      if (i === 0) {
        // Título en la primera página
        pdf.setFontSize(22);
        pdf.setTextColor(0, 0, 0);
        pdf.text('', pageW / 2, 20, { align: 'center' });
      }


      const { width: svgWidth, height: svgHeight, viewBox, svgElement } = this.parseSvgSize(curveData.svg);
      if (svgWidth === 0 || svgHeight === 0) continue;

      const vbWidthMm = pixelspacing.width * svgWidth;
      const vbHeightMm = pixelspacing.height * svgHeight;

      // 1. Wrap content in a group to rotate
      const svgNS = 'http://www.w3.org/2000/svg';
      const g = svgElement.ownerDocument.createElementNS(svgNS, 'g');
      while (svgElement.firstChild) {
        g.appendChild(svgElement.firstChild);
      }
      // Rotate 90 deg clockwise and translate to keep it in view

      g.setAttribute('transform', `translate(${svgHeight}, 0) rotate(90)`);
      svgElement.appendChild(g);

      // 2. Update SVG attributes to reflect the new orientation
      svgElement.setAttribute('width', svgHeight.toString());
      svgElement.setAttribute('height', svgWidth.toString());
      svgElement.setAttribute('viewBox', `0 0 ${svgHeight} ${svgWidth}`);
      svgElement.setAttribute('preserveAspectRatio', 'xMinYMid meet');

      // 3. New dimensions for positioning (already rotated)
      const rotW = vbHeightMm;
      const rotH = vbWidthMm;

      // Escalar para que rotH ocupe ~92% del alto de página
      const scale = 1;
      const marginRight = 10;

      const x = pageW - marginRight - (rotW * scale);
      const y = (pageH - (rotH * scale)) / 2;

      console.log(`SVG Info: Width=${svgWidth}, Height=${svgHeight}, rotW=${rotW}, rotH=${rotH}`);
      console.log(`PDF Position: x=${x}, y=${y}, scale=${scale}`);

      // pdf.svg works with current jsPDF unit (mm)
      await pdf.svg(svgElement, {
        x: x,
        y: y,
        width: rotW * scale,
        height: rotH * scale
      });

      const lineSpacing = 5;
      const blockHeight = lineSpacing * Math.max(0, curveData.text.length - 1);
      const blockWidth = lineSpacing * Math.max(0, curveData.text.length - 1);
      const flapPadding = 2;
      const flapWidth = Math.max(20, blockWidth + flapPadding * 2);

      // Dibujar línea de medida (a la izquierda de la curva)
      const flapLeft = Math.max(0, x - flapWidth);
      const lineX = x;
      const innerStartXAttr = svgElement.getAttribute('data-inner-start-x');
      const innerEndXAttr = svgElement.getAttribute('data-inner-end-x');
      let lineY1: number;
      let lineY2: number;

      if (innerStartXAttr !== null && innerEndXAttr !== null) {
        const innerStartX = parseFloat(innerStartXAttr);
        const innerEndX = parseFloat(innerEndXAttr);
        const innerStartMm = innerStartX * pixelspacing.width;
        const innerEndMm = innerEndX * pixelspacing.width;
        const minMm = Math.min(innerStartMm, innerEndMm);
        const maxMm = Math.max(innerStartMm, innerEndMm);
        lineY1 = y + minMm * scale;
        lineY2 = y + maxMm * scale;
      } else {
        const extraFlap = 10; // mm adicionales arriba y abajo
        lineY1 = y - extraFlap;
        lineY2 = y + rotH * scale + extraFlap;
      }

      // Asegurar que la línea quede dentro de la página
      lineY1 = Math.max(0, lineY1);
      lineY2 = Math.min(pageH, lineY2);

      const flapRight = x;
      const curveLeft = x;
      const curveRight = x + (rotW * scale);
      const horizontalPadding = 2;
      const desiredInset = Math.min(12, (rotW * scale) * 0.15);
      const desiredTextX = curveLeft + desiredInset + blockWidth + 60;
      const minTextX = curveLeft + horizontalPadding + blockWidth;
      const maxTextX = curveRight - horizontalPadding;
      const textX = Math.max(minTextX, Math.min(desiredTextX, Math.max(minTextX, maxTextX)));
      const verticalPadding = 2;
      const desiredTextY = ((lineY1 + lineY2) / 2) - 3 - 40;
      const minTextY = lineY1 + verticalPadding;
      const maxTextY = lineY2 - verticalPadding;
      const textY = Math.max(minTextY, Math.min(desiredTextY, Math.max(minTextY, maxTextY)));
      this.drawTextBlock(pdf, curveData.text, curveData.color, x + svgHeight / 2 + 20 ,  y + svgWidth / 3 - 40 , { angle: -90 });

      pdf.saveGraphicsState();
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);

      //COTA
      pdf.line(lineX, lineY1, lineX, lineY2);
      // Ticks
      pdf.line(lineX - 1.5, lineY1, lineX + 1.5, lineY1);
      pdf.line(lineX - 1.5, lineY2, lineX + 1.5, lineY2);
      // Texto de medida (alto total de la "solapa")
      pdf.setFontSize(10);
      const measuredHeight = (lineY2 - lineY1);
      const measureText = `${measuredHeight.toFixed(1)} mm`;
      pdf.text(measureText, lineX - 2, (lineY1 + lineY2) / 2, { angle: 90, align: 'center' });
      pdf.restoreGraphicsState();
    }

    return new Uint8Array(pdf.output('arraybuffer'));
  }

  private async mergeBackgroundWithOverlay(bgBytes: ArrayBuffer, overlayBytes: Uint8Array, fileName: string): Promise<Blob> {
    const bgDoc = await PDFDocument.load(bgBytes);
    const ovDoc = await PDFDocument.load(overlayBytes);

    const outDoc = await PDFDocument.create();
    outDoc.setTitle(fileName);
    outDoc.setAuthor('ScanMed');
    outDoc.setSubject(fileName);
    outDoc.setProducer('ScanMed');
    outDoc.setCreator('ScanMed');
    outDoc.setCreationDate(new Date());
    outDoc.setModificationDate(new Date());
    const { width: bgW, height: bgH } = bgDoc.getPages()[0].getSize();
    const totalPages = ovDoc.getPageCount();
    const pageNumberSize = 9;
    const pageNumberMargin = 12;

    // Páginas 1..N: Background + Overlay
    for (let i = 0; i < totalPages; i++) {
      const [bgPage] = await outDoc.copyPages(bgDoc, [0]);
      outDoc.addPage(bgPage);

      const embedded = await outDoc.embedPage(ovDoc.getPage(i));
      bgPage.drawPage(embedded, {
        x: 0,
        y: 0,
        width: bgW,
        height: bgH
      });

      const pageNumberText = `${i + 1}/${totalPages}`;
      const estimatedCharWidth = pageNumberSize * 0.55;
      const pageNumberWidth = pageNumberText.length * estimatedCharWidth;
      bgPage.drawText(pageNumberText, {
        x: bgW - pageNumberWidth - pageNumberMargin,
        y: bgH - pageNumberSize - pageNumberMargin,
        size: pageNumberSize,
      });
    }

    const pdfBytes = await outDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  }

  private implantLabel(label: string) {
    if(label === 'INF'){
      return "Inferior"
    }
    if(label === 'MED'){
      return "Medio"
    }
    if(label === 'SUP'){
      return "Superior"
    }
    if(label === 'EXT'){
      return "Externo"
    }
    // TC-PDF-014: variantes compuestas (medio superior / medio inferior).
    if(label === 'MED SUP'){
      return "Medio Superior"
    }
    if(label === 'MED INF'){
      return "Medio Inferior"
    }
    return ''
  }

  /**
   * TC-PDF-014: extrae el código de implante del final del cartel
   * `"PATIENT - LABEL"`. Antes se hacía `lines[0].slice(-3)` lo cual sólo
   * funciona con labels de 3 chars (SUP/MED/INF/EXT) — rompe con
   * `MED SUP` / `MED INF` (7 chars). Parseamos desde el último " - " para
   * soportar labels de cualquier largo y no asumir 3 chars.
   */
  private extractImplantLabel(line: string): string {
    if (!line) return '';
    const sepIdx = line.lastIndexOf(' - ');
    if (sepIdx === -1) return line.trim();
    return line.substring(sepIdx + 3).trim();
  }

  private drawTextBlock(
    pdf: jsPDF,
    lines: string[],
    color: string,
    x: number,
    y: number,
    options?: { angle?: number }
  ): void {
    const lineSpacing = 5;

    pdf.setDrawColor(color);
    pdf.setLineWidth(0.4);

    pdf.setTextColor(color);

    let xPos = x;
    pdf.setTextColor('black');
    pdf.setFontSize(14);
    pdf.text(lines[0].slice(0,-5), 32, 12.8);
    pdf.setFontSize(12);
    pdf.text(lines[1], 30, 20.2);
    // La X del checkbox de ranura: si el llamador pasó override (caller pasa
    // variant por parámetro a exportSvgToPdf, ej. desde el editor ERKOM), lo
    // usamos; si no, fallback al sessionStorage (drawer del visualizador).
    // v2 = Titanio (y=41.4), v1 / default = Acero (y=33.2).
    const nussVariant = this._nussVariantOverride ?? sessionStorage.getItem('nuss_template_variant');
    if (nussVariant === 'v2') {
      pdf.text("X", 55.5, 37.4);
    } else {
      pdf.text("X", 55.5, 29.2);
    }
    pdf.setFontSize(16);
    // TC-PDF-014: extraer el label parseando desde el último " - " del cartel
    // (`PATIENT - LABEL`) para soportar labels de cualquier largo, incluidos
    // `MED SUP` / `MED INF`.
    const implantCode = this.extractImplantLabel(lines[0]);
    pdf.text(this.implantLabel(implantCode), 30, 60);
    //roundImplantLength(222,lines[1].slice(0,-13))
    const largeValue = getMiddleValue(lines[3])|| '0';
    const valueRounded = roundImplantLength(parseInt(largeValue), implantCode)
    pdf.setFontSize(16);
    pdf.text(String(valueRounded), 30, 51);


  }



  sendPdfErkom(
    orderId: number,
    procedureId: number,
    implantTypeId: number,
    quantity: number,
    file: any
  ): Observable<any> {

    const body = {
      procedure_id: procedureId,
      implant_type_id: implantTypeId,
      quantity: quantity,
      file: file,
    };
    return this._http.post(
      `${this._apiUrl}/api/order/${orderId}/upload-implant-document`,
      body,
      {
        headers: this._authHeaders,
      }
    );
  }
}
